import type { ReactNode } from 'react';
import { localDate } from '../lib/dates';
import { useI18n } from '../lib/i18n';
import type { ScheduleItem } from '../lib/types';
import { Badge, cx, Empty } from './components';

export function SpotsBadge({ item }: { item: ScheduleItem }) {
  const i18n = useI18n();
  if (item.myBooking) return <Badge value={item.myBooking.status} label={item.myBooking.status === 'waitlisted' ? i18n.t('planner.waitlist_position', { position: item.myBooking.waitlistPosition ?? '?' }) : undefined} />;
  if (item.spotsLeft === 0) return <span className="badge badge-bad">{i18n.t('planner.full')}{item.waitlist ? ` · ${i18n.t('planner.waiting', { count: item.waitlist })}` : ''}</span>;
  return <span className={cx('badge', item.spotsLeft <= 2 ? 'badge-warn' : 'badge-neutral')}>{i18n.t('planner.spots_left', { count: item.spotsLeft, capacity: item.capacity })}</span>;
}

export function LessonRow({ item, onClick, right }: { item: ScheduleItem; onClick?: () => void; right?: ReactNode }) {
  const i18n = useI18n();
  return (
    <button type="button" className={cx('lesson-row', item.myBooking && 'mine')} onClick={onClick} style={{ borderLeftColor: item.lesson.color ?? 'var(--brand)' }}>
      <div className="lesson-time"><strong>{i18n.time(item.startsAt)}</strong><small>{i18n.time(item.endsAt)}</small></div>
      <div className="lesson-main">
        <strong>{item.lesson.name}</strong>
        <small className="muted">{[item.location.name, item.room, item.trainer?.name].filter(Boolean).join(' · ')}</small>
      </div>
      <div className="lesson-right">{right ?? <SpotsBadge item={item} />}</div>
    </button>
  );
}

export function groupByDay<T extends { startsAt: string }>(items: T[], tz: string): [string, T[]][] {
  const out = new Map<string, T[]>();
  for (const it of items) {
    const d = localDate(it.startsAt, tz);
    if (!out.has(d)) out.set(d, []);
    out.get(d)!.push(it);
  }
  return [...out.entries()].sort(([a], [b]) => a.localeCompare(b));
}

export function ScheduleList({ items, onPick, empty, right }: { items: ScheduleItem[]; onPick?: (i: ScheduleItem) => void; empty?: ReactNode; right?: (i: ScheduleItem) => ReactNode }) {
  const i18n = useI18n();
  if (!items.length) return <Empty icon="📅">{empty ?? i18n.t('planner.no_lessons')}</Empty>;
  return (
    <div className="stack">
      {groupByDay(items, i18n.tz).map(([day, list]) => (
        <section key={day} className="day-group">
          <h3 className="day-title">{i18n.day(day, { weekday: 'long', day: 'numeric', month: 'long' })}</h3>
          <div className="stack-sm">{list.map((it) => <LessonRow key={it.id} item={it} onClick={onPick ? () => onPick(it) : undefined} right={right?.(it)} />)}</div>
        </section>
      ))}
    </div>
  );
}

export function PlanPrice({ cents, interval, count }: { cents: number; interval: string; count?: number | null }) {
  const i18n = useI18n();
  const per = interval === 'none' ? '' : ` / ${count && count > 1 ? `${count} ` : ''}${i18n.t(`interval.${interval}`)}`;
  return <span className="price">{i18n.money(cents)}<small>{per}</small></span>;
}
